import { useCallback, useRef } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'

import { useLenis } from '@/contexts/lenis'
import { useMotionMatchMedia, useMotionTouchMedia } from '@/hooks'
import { useLoaderStore } from '@/store/loader'

import {
  createLinesMotion,
  createMaskMotion,
  createWordsMotion,
  getScrollTriggerOptions,
  Motion
} from './shared'

export const useHomeHumanMotion = () => {
  const motionRef = useRef<HTMLDivElement>(null)
  const revealScrollTrigger = useRef<ScrollTrigger>(null!)
  const parallaxScrollTrigger = useRef<ScrollTrigger>(null!)
  const fontsReady = useLoaderStore((state) => state.fontsReady)
  const { lenis } = useLenis()

  const titleMotion = useRef<Motion | null>(null)
  const pretitleMotion = useRef<Motion | null>(null)
  const paragraphMotion = useRef<Motion | null>(null)

  const revertSplitText = useCallback(() => {
    titleMotion.current?.revert()
    pretitleMotion.current?.revert()
    paragraphMotion.current?.revert()
  }, [])

  /**
   * reveal setup
   */
  useMotionTouchMedia(
    {
      pointer({ selector }) {
        if (!fontsReady || !lenis) return

        const rows = selector?.('[data-motion-row]')
        const title = selector?.('[data-motion-title]')[0]
        const pretitle = selector?.('[data-motion-pretitle]')[0]
        const paragraph = selector?.('[data-motion-paragraph]')[0]

        titleMotion.current = title ? createWordsMotion(title) : null
        pretitleMotion.current = pretitle ? createMaskMotion(pretitle) : null
        paragraphMotion.current = paragraph ? createLinesMotion(paragraph) : null

        const animation = gsap.timeline({ onComplete: revertSplitText })
        titleMotion.current && animation.add(titleMotion.current.timeline, 0)
        pretitleMotion.current && animation.add(pretitleMotion.current.timeline, 0.1)
        paragraphMotion.current && animation.add(paragraphMotion.current.timeline, 0.1)

        revealScrollTrigger.current = ScrollTrigger.create({
          ...getScrollTriggerOptions({
            trigger: rows[0],
            animation
          })
        })
      },

      revert() {
        revealScrollTrigger.current?.kill()
        revertSplitText()
      }
    },
    { scope: motionRef, dependencies: [fontsReady, lenis] }
  )

  /**
   * parallax setup
   */
  useGSAP(
    () => {
      if (!lenis) return

      const scrollTrigger = ScrollTrigger.create({
        trigger: '[data-motion-media]',
        start: 'top bottom',
        markers: false,
        scrub: true,
        pin: true,
        pinSpacing: false,
        animation: gsap.fromTo(
          '[data-motion-media]>div',
          { y: '-100vh' },
          { y: '-100vh', duration: 1, ease: 'none' }
        )
      })

      return () => {
        scrollTrigger.kill()
      }
    },
    { scope: motionRef, dependencies: [lenis] }
  )

  /**
   * parallax setup
   */
  useMotionMatchMedia(
    {
      medium({ selector }) {
        if (!lenis) return

        const rows = selector?.('[data-motion-row]')

        parallaxScrollTrigger.current = ScrollTrigger.create({
          trigger: motionRef.current,
          start: 'top bottom',
          end: 'bottom top',
          markers: false,
          scrub: true,
          animation: gsap
            .timeline({})
            .fromTo(
              rows[1],
              { yPercent: (index) => (index === 0 ? 0 : index === 1 ? 0 : 0) },
              {
                yPercent: (index) => (index === 0 ? 50 : index === 1 ? 0 : 0),
                duration: 1,
                ease: 'none'
              }
            )
            .fromTo(
              '[ data-motion-paragraph-media]',
              { yPercent: -20 },
              { yPercent: 0, duration: 1, ease: 'none' },
              0
            )
        })
      },

      revert() {
        parallaxScrollTrigger.current?.kill()
      }
    },
    { scope: motionRef, dependencies: [lenis] }
  )

  return { motionRef }
}
